import {
  ComplexFieldConfig,
  FieldSchema,
  FieldSchemaFormBuilderV2,
  FieldSchemas,
  Schema,
} from "./types";
import ApiClient from "../../lib/apiClient";
import FieldSchemaApiClient from "./fieldSchemaApiClient";
import {
  BaseFieldProps,
  CoreFieldProps,
  CoreFieldValue,
  FieldType,
} from "../Fields/types/fieldTypes";
import { get, set } from "lodash";
import { ComplexFieldProps } from "../Fields/CoreFields/ComplexField/ComplexField";
import { getErrorMessagesFromFormSchemaBuilderErrors } from "../../utils/field";

export interface FieldSchemaFormBuilderConfigV2 {
  model: string;
  schema: (string | BaseFieldProps<CoreFieldValue>)[];
  required?: string[];
  complexFieldConfig?: ComplexFieldConfig;
}

export interface FieldSchemaErrorV2 {
  data: { [key: string]: string[] };
}

export class SchemaFactoryV2 {
  public schema: Schema | null = null;

  constructor(loadedSchema: Schema | null) {
    this.schema = loadedSchema;
  }

  getSchemaProperties(modelName: string): string[] {
    if (this.schema === null) {
      throw new Error("Schema is not initialized");
    }

    let modelSchema = get(this.schema, `schema.${modelName}`);

    if (!modelSchema) {
      modelSchema = get(this.schema, `Schema.${modelName}`);
      if (!modelSchema) {
        throw new Error(`Missing Model Schema ${modelName}`);
      }
    }

    return Object.keys(modelSchema);
  }

  getFieldSchemaGetter(
    modelName: string,
    errors: { [key: string]: string[] } = {}
  ) {
    const getFieldKey = (key: string) => `${modelName}__${key}`;

    return (fieldKey: string) => ({
      ...this.getFieldSchemaByName(modelName, getFieldKey(fieldKey)),
      errorMessages: getErrorMessagesFromFormSchemaBuilderErrors(
        errors,
        fieldKey
      ),
    });
  }

  getFieldSchemas(): { [key: string]: FieldSchemas } {
    if (this.schema === null) {
      throw new Error("Schema is not initialized");
    }
    return get(this.schema, "schema");
  }

  // Get the field schema for a given model and field name
  getFieldSchemaByName(
    modelName: string,
    fieldKey: string,
    complexFieldConfig?: ComplexFieldConfig
  ): FieldSchema {
    // If the schema is not initialized, throw an error
    if (this.schema === null) {
      throw new Error("Schema is not initialized");
    }

    // Get the field schema from the schema
    let fieldSchema: FieldSchema | undefined = get(
      this.schema,
      `schema.${modelName}.${fieldKey}`
    );

    if (!fieldSchema) {
      fieldSchema = get(this.schema, `Schema.${modelName}.${fieldKey}`);
      if (!fieldSchema) {
        throw new Error(`Missing Field Schema ${modelName}.${fieldKey}`);
      }
    }

    // Process Complex
    if (fieldSchema.type_AsString === "Complex") {
      fieldSchema = this.getComplexSchema(fieldSchema, complexFieldConfig);
    }

    // Return the field schema with the type_AsString cast to a FieldType
    return {
      ...fieldSchema,
      ...{ type: fieldSchema.type_AsString as FieldType },
    };
  }

  getComplexSchema(
    fieldSchema: FieldSchema,
    complexFieldConfig?: ComplexFieldConfig
  ): FieldSchema {
    // todo check if this cast works
    let complexSchema = fieldSchema as ComplexFieldProps<
      Partial<CoreFieldProps>
    >;

    if (!fieldSchema.fieldKey) {
      throw new Error(
        `Missing Field Key on Complex Field Schema ${fieldSchema.fieldKey}`
      );
    }

    if (!complexFieldConfig) {
      throw new Error(
        `Missing complexFieldConfig to render Complex Field Schema ${fieldSchema.fieldKey}`
      );
    }

    if (!fieldSchema.fieldTypeName) {
      throw new Error(
        `Missing Metadata on Complex Field Schema ${fieldSchema.fieldKey}`
      );
    }

    const FieldComponent = complexFieldConfig[
      fieldSchema.fieldTypeName
    ] as React.FC<Partial<CoreFieldProps>>;

    if (!FieldComponent) {
      // Return a hidden div and log an error so the developer can fix it
      console.error(
        `Missing Field Component for Complex Field Schema ${fieldSchema.fieldKey}`
      );
      complexSchema.renderer = () => <div style={{ display: "none" }} />;
      return complexSchema;
    }

    complexSchema.fieldKey = fieldSchema.fieldKey;
    complexSchema.renderer = FieldComponent as any;

    return complexSchema;
  }

  /*
   * Build a model schema based on the provided model name
   * @param modelName - The name of the model to build the schema for
   * @param onValueChange - A function that will be called when a value for a field changes
   * @param form - The current form object
   * @param errors - An object containing any errors for the form
   * @param requiredFields - An array of required fields for the form
   */
  buildModelSchema<TModel extends Object>(
    modelName: string,
    onValueChange: (
      newValue: TModel,
      newFieldValue: CoreFieldValue,
      fieldProps: BaseFieldProps<CoreFieldValue>
    ) => void,
    form: TModel,
    errors?: { [key: string]: string[] },
    requiredFields?: string[],
    complexFieldConfig?: ComplexFieldConfig,
    fieldSet?: string
  ): BaseFieldProps<CoreFieldValue>[] {
    const fieldKeys = this.getSchemaProperties(modelName);

    let fields = this.buildFieldSchemas<TModel>(
      modelName,
      fieldKeys,
      onValueChange,
      form,
      errors,
      requiredFields,
      complexFieldConfig
    );

    if (fieldSet) {
      fields = fields.filter((field) => {
        return field.fieldSet === fieldSet;
      });
    }

    return fields;
  }

  buildFieldSchema<TModel extends Object>(
    modelName: string,
    fieldKey: string,
    onValueChange: (
      newValue: TModel,
      newFieldValue: CoreFieldValue,
      fieldProps: BaseFieldProps<CoreFieldValue>
    ) => void,
    form: TModel,
    errors?: { [key: string]: string[] },
    isRequired?: boolean,
    complexFieldConfig?: ComplexFieldConfig
  ): BaseFieldProps<CoreFieldValue> {
    // Get Schema
    const fieldSchema = this.getFieldSchemaByName(
      modelName,
      fieldKey,
      complexFieldConfig
    );
    const field = fieldSchema as BaseFieldProps<CoreFieldValue>;

    if (!field.fieldName) {
      throw new Error(
        `Component with Field Key ${field.fieldKey} does not have required fieldName property assigned.`
      );
    }
    if (!fieldSchema.fieldPath) {
      throw new Error(
        `Component with Field Key ${field.fieldKey} does not have required fieldPath property assigned.`
      );
    }

    const fieldName = field.fieldName;
    field.modelName = modelName;
    field.value = get(form, fieldSchema.fieldPath);
    field.errorMessages = getErrorMessagesFromFormSchemaBuilderErrors(
      errors,
      fieldName,
      fieldSchema.fieldPath
    );

    field.isRequired = isRequired;
    field.onSubmit = (value: CoreFieldValue) => {
      if (!fieldSchema.fieldPath) {
        throw new Error(
          `Component with Field Key ${field.fieldKey} does not have required fieldPath property assigned.`
        );
      }

      const newValue = { ...form };
      set(newValue, fieldSchema.fieldPath, value);
      onValueChange({ ...newValue }, value, field);
    };

    // If the field is a complex field, we pass the form value to the complex field
    if (field.type_AsString === "Complex") {
      set(field, "formValue", { ...form });
      set(field, "onValueChange", onValueChange);
    }

    return field;
  }

  buildFieldSchemas<TModel extends Object>(
    modelName: string,
    fieldKeys: (string | BaseFieldProps<CoreFieldValue>)[],
    onValueChange: (
      newValue: TModel,
      newFieldValue: CoreFieldValue,
      fieldProps: BaseFieldProps<CoreFieldValue>
    ) => void,
    form: TModel,
    errors?: { [key: string]: string[] },
    requiredFields?: string[],
    complexFieldConfig?: ComplexFieldConfig
  ): BaseFieldProps<CoreFieldValue>[] {
    const fieldSchemas = fieldKeys.map((fieldKey) => {
      if (typeof fieldKey === "string") {
        const field = this.buildFieldSchema<TModel>(
          modelName,
          fieldKey,
          onValueChange,
          form,
          errors,
          requiredFields?.includes(fieldKey),
          complexFieldConfig
        );
        return field;
      } else {
        // If it's an object, it's a field schema.
        // If it has a fieldKey assigned we usually auto build the field schema
        // and merge the two objects. UI-only items like Sections should not
        // attempt to hydrate from backend schema, so we skip auto-build for them.
        if (fieldKey.fieldKey && fieldKey.type !== "Section") {
          // Build the field schema
          const field = this.buildFieldSchema<TModel>(
            fieldKey?.modelName || modelName,
            fieldKey.fieldKey,
            onValueChange,
            form,
            errors,
            requiredFields?.includes(fieldKey.fieldKey),
            complexFieldConfig
          );
          // Merge the two objects
          return { ...field, ...fieldKey };
        }
        // Return the object as is
        return fieldKey;
      }
    });
    return fieldSchemas;
  }

  buildFieldSchemaFormBuilder<TModel extends Object>(
    config: FieldSchemaFormBuilderConfigV2
  ): FieldSchemaFormBuilderV2<TModel> {
    const builder: FieldSchemaFormBuilderV2<TModel> = (
      onValueChange,
      form,
      errors
    ) => {
      return this.buildFieldSchemas<TModel>(
        config.model,
        config.schema,
        onValueChange,
        form,
        errors,
        config.required,
        config.complexFieldConfig
      );
    };

    return builder;
  }
}

export const useFieldSchemaV2 = (route?: string) => {
  const getAsyncSchemaFactory = async (apiClient: ApiClient) => {
    const fieldSchemaApiClient = new FieldSchemaApiClient(apiClient);
    const fetchedSchema = await fieldSchemaApiClient.getSchemaV2(route);
    return new SchemaFactoryV2(fetchedSchema);
  };

  const getSchemaFactory = async (schema: Schema) => {
    return new SchemaFactoryV2(schema);
  };

  return { getAsyncSchemaFactory, getSchemaFactory };
};
